import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Building2, Users, FileText, Activity, AlertCircle, CheckCircle } from 'lucide-react';

export default function AdminOverviewPage() {
  const [overview, setOverview] = useState(null);
  const [tenantUsage, setTenantUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/overview'),
      api.get('/admin/tenants-usage'),
    ]).then(([o, t]) => {
      setOverview(o.data);
      setTenantUsage(t.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#A3A3A3] font-ibm">Loading admin overview...</div>;
  if (!overview) return <div className="text-[#737373] font-ibm">Failed to load admin data</div>;

  const topStats = [
    { label: 'Bayiler', value: overview.tenants.total, sub: `${overview.tenants.active} aktif`, icon: Building2, color: '#FACC15' },
    { label: 'Kullanıcılar', value: overview.users.total, icon: Users, color: '#3B82F6' },
    { label: 'İçerik Varlıkları', value: overview.content.total, icon: FileText, color: '#10B981' },
    { label: 'Toplam İşler', value: overview.jobs.total, icon: Activity, color: '#F59E0B' },
  ];

  const jobStats = [
    { label: 'Bekleyen', value: overview.jobs.pending, color: '#F59E0B' },
    { label: 'İşleniyor', value: overview.jobs.processing, color: '#3B82F6' },
    { label: 'Tamamlanan', value: overview.jobs.done, color: '#10B981' },
    { label: 'Başarısız', value: overview.jobs.failed, color: '#EF4444' },
  ];

  return (
    <div data-testid="admin-overview-page" className="animate-fade-in space-y-6">
      <div className="pb-6 border-b border-[#262626]">
        <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Admin Paneli</h1>
        <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Sistem genelinde platform metrikleri</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {topStats.map((s) => (
          <Card key={s.label} className="stat-card" data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#737373] text-sm font-ibm">{s.label}</p>
                  <p className="text-3xl font-outfit font-bold text-white mt-1">{s.value}</p>
                  {s.sub && <p className="text-xs text-[#525252] font-ibm mt-0.5">{s.sub}</p>}
                </div>
                <div className="p-3 rounded-lg" style={{ background: `${s.color}15` }}>
                  <s.icon size={20} style={{ color: s.color }} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {jobStats.map((s) => (
          <Card key={s.label} className="bg-[#141414] border-[#262626]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
              <div>
                <p className="text-xs text-[#737373] font-ibm">{s.label} İşler</p>
                <p className="text-xl font-outfit font-bold text-white">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {overview.ai_usage && (
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white">AI Kullanım İstatistikleri</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { key: 'text_generation', label: 'Metin', color: '#FACC15' },
                { key: 'image_generation', label: 'Görsel', color: '#3B82F6' },
                { key: 'audio_generation', label: 'Ses', color: '#10B981' },
                { key: 'subtitle_generation', label: 'Altyazı', color: '#F59E0B' },
                { key: 'video_generation', label: 'Video', color: '#EF4444' },
              ].map(s => (
                <div key={s.key} className="p-4 bg-[#0A0A0A] rounded-md border border-[#1A1A1A] text-center">
                  <p className="text-2xl font-outfit font-bold" style={{ color: s.color }}>{overview.ai_usage[s.key] || 0}</p>
                  <p className="text-xs text-[#737373] font-ibm mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white">Bayi Kullanımı</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-[#262626] hover:bg-transparent">
                  <TableHead className="text-[#A3A3A3] font-ibm">Bayi</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">Plan</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm text-right">Kullanıcılar</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm text-right">İçerik</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm text-right">İşler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenantUsage.map((t) => (
                  <TableRow key={t.tenant_id} className="border-[#262626] hover:bg-white/5">
                    <TableCell className="font-ibm text-white">
                      <div className="flex items-center gap-2">
                        {t.name}
                        {!t.is_active && <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">Devre Dışı</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20 capitalize text-xs">{t.plan_type}</Badge>
                    </TableCell>
                    <TableCell className="text-right text-[#A3A3A3] font-jetbrains text-sm">{t.users_count}</TableCell>
                    <TableCell className="text-right text-[#A3A3A3] font-jetbrains text-sm">{t.content_count}</TableCell>
                    <TableCell className="text-right text-[#A3A3A3] font-jetbrains text-sm">{t.jobs_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white">Son Etkinlik</CardTitle></CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              {(!overview.recent_activity || overview.recent_activity.length === 0) && (
                <p className="text-[#737373] text-sm font-ibm py-4">Henüz etkinlik yok</p>
              )}
              <div className="space-y-2">
                {overview.recent_activity?.map((log, i) => (
                  <div key={i} className="p-3 bg-[#0A0A0A] rounded-md border border-[#1A1A1A]">
                    <div className="flex items-center gap-2">
                      {log.details?.status === 'success'
                        ? <CheckCircle size={14} className="text-green-400 shrink-0" />
                        : <AlertCircle size={14} className="text-red-400 shrink-0" />
                      }
                      <span className="text-sm text-white font-ibm">{log.action?.replace('_', ' ')}</span>
                    </div>
                    <p className="text-xs text-[#737373] font-jetbrains mt-1">
                      {log.details?.content_type?.replace('_', ' ')} - {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
